import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { 
  LayoutDashboard, 
  FileText, 
  FileCheck, 
  Wallet, 
  Calculator, 
  Building2, 
  Shield, 
  PiggyBank, 
  ClipboardCheck,
  Gift,
  Settings,
  LogOut,
  Lock,
  Menu,
  X,
  ChevronRight,
  Info,
  Scale,
  Inbox
} from 'lucide-react';

const navItems = [
  { 
    section: 'TABLEAU DE BORD',
    borderColor: 'border-l-4 border-l-[#10B981]',
    bgColor: 'bg-[#10B981]/5',
    items: [
      { path: '/dashboard', label: 'Vue d\'ensemble', icon: LayoutDashboard }
    ]
  },
  {
    section: 'GESTION',
    borderColor: 'border-l-4 border-l-blue-500',
    bgColor: 'bg-blue-50',
    items: [
      { path: '/profil-entreprise', label: 'Mon entreprise', icon: Building2 },
      { path: '/factures', label: 'Factures', icon: FileText },
      { path: '/factures-recues', label: 'Factures reçues', icon: Inbox, badge: '2026' },
      { path: '/devis', label: 'Devis', icon: FileCheck },
      { path: '/depenses', label: 'Dépenses', icon: Wallet },
    ]
  },
  {
    section: 'FISCALITÉ',
    borderColor: 'border-l-4 border-l-amber-500',
    bgColor: 'bg-amber-50',
    items: [
      { path: '/tva', label: 'TVA', icon: Calculator },
      { path: '/urssaf', label: 'Cotisations URSSAF', icon: Building2 },
      { path: '/regles', label: 'Règles TVA vs URSSAF', icon: Scale },
    ]
  },
  {
    section: 'PROTECTION',
    borderColor: 'border-l-4 border-l-emerald-500',
    bgColor: 'bg-emerald-50',
    items: [
      { path: '/protection-sociale', label: 'Protection sociale', icon: Shield },
      { path: '/epargne-retraite', label: 'Épargne retraite', icon: PiggyBank },
      { path: '/assurance', label: 'Assurance', icon: ClipboardCheck },
    ]
  },
  {
    section: 'RESSOURCES',
    borderColor: 'border-l-4 border-l-purple-500',
    bgColor: 'bg-purple-50',
    items: [
      { path: '/infos-pratiques', label: 'Infos pratiques', icon: Info },
      { path: '/bonus', label: 'Patrithèque', icon: Gift },
    ]
  }
];

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const firstName = user?.prenom || user?.first_name || user?.email?.split('@')[0] || 'Client';

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.path;
    return (
      <Link
        to={item.path}
        onClick={() => setSidebarOpen(false)}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 group ${
          isActive 
            ? 'bg-[#10B981] text-white shadow-md' 
            : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
        }`}
        data-testid={`nav-${item.path.slice(1)}`}
      >
        <item.icon className={`h-5 w-5 ${isActive ? '' : 'group-hover:text-[#10B981]'}`} />
        <span className="font-medium flex-1">{item.label}</span>
        {item.badge && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
            isActive ? 'bg-white/20 text-white' : 'bg-blue-100 text-blue-700'
          }`}>
            {item.badge}
          </span>
        )}
        {isActive && !item.badge && <ChevronRight className="h-4 w-4 ml-auto" />}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b px-4 py-3 flex items-center justify-between">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => setSidebarOpen(true)}
          data-testid="mobile-menu-btn"
        >
          <Menu className="h-6 w-6" />
        </Button>
        <Link to="/dashboard" className="font-bold text-lg">
          <span className="text-[#10B981]">Tableau de bord</span> Mon AE
        </Link>
        <div className="w-10" />
      </header>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-white border-r transform transition-transform duration-300 ease-in-out
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-5 border-b flex items-center justify-between">
            <Link to="/dashboard" className="font-bold text-lg">
              <span className="text-[#10B981]">Tableau de bord</span> Mon AE
            </Link>
            <Button 
              variant="ghost" 
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 p-3">
            <nav className="space-y-4">
              {navItems.map((group) => (
                <div key={group.section}>
                  <h3 className={`text-xs font-bold tracking-wider mb-2 px-3 py-2 rounded-lg ${group.borderColor} ${group.bgColor}`}>
                    {group.section}
                  </h3>
                  <div className="space-y-1">
                    {group.items.map((item) => (
                      <NavLink key={item.path} item={item} />
                    ))}
                  </div>
                </div>
              ))}
              
              {/* Admin section - visible pour admin uniquement */}
              {user?.is_admin && (
                <div>
                  <h3 className="text-xs font-bold tracking-wider mb-2 px-3 py-2 rounded-lg border-l-4 border-l-red-500 bg-red-50">
                    ADMINISTRATION
                  </h3>
                  <div className="space-y-1">
                    <Link
                      to="/admin"
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 group ${
                        location.pathname === '/admin'
                          ? 'bg-[#10B981] text-white shadow-md' 
                          : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                      }`}
                      data-testid="nav-admin"
                    >
                      <Settings className="h-5 w-5" />
                      <span className="font-medium">Paramètres</span>
                    </Link>
                  </div>
                </div>
              )}
            </nav>
          </ScrollArea>

          {/* User section */}
          <div className="p-4 border-t bg-gray-50">
            <div className="flex items-center gap-3 mb-3 px-2">
              <div className="w-9 h-9 rounded-full bg-[#10B981]/10 flex items-center justify-center text-[#10B981] font-semibold text-sm">
                {firstName[0]?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate text-sm text-gray-800">
                  {user?.first_name || firstName} {user?.last_name || ''}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
            <Link
              to="/change-password"
              onClick={() => setSidebarOpen(false)}
              className="w-full flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md mb-2"
              data-testid="nav-change-password"
            >
              <Lock className="h-4 w-4 mr-2" />
              Changer mon mot de passe
            </Link>
            <Button 
              variant="outline" 
              className="w-full justify-start text-gray-600 hover:text-gray-900" 
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:pl-64">
        <div className="pt-16 lg:pt-0 min-h-screen">
          <div className="p-5 lg:p-8">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Layout;
